/**
 * The text behind "Butiran teknikal" on the error cards — what a seller pastes
 * to us. It has to settle a crash from the paste alone: with ~600 sellers there
 * is no time for a round of "which store? when? did it happen again after
 * reloading?". Every line answers one of those questions.
 */

/** sessionStorage key holding when this tab last auto-reloaded to recover a chunk. */
export const AUTO_RELOAD_KEY = "chunk-reload-at";

/** What the browser holds, and what the server now sends, for one failed build file. */
export interface AssetProbe {
  url: string;
  /** e.g. "200 text/html", or "tiada" when nothing is cached. */
  cached: string;
  network: string;
}

export interface ErrorReportInput {
  name: string;
  message: string;
  stack?: string;
  /** React's component stack from componentDidCatch — which component threw. */
  componentStack?: string;
  /** pathname + search of the page that crashed. */
  path: string;
  /** When the boundary caught it (epoch ms). */
  at: number;
  /** Commit the running page was built from (meta app-build). */
  build: string | null;
  /** Build the server is serving right now: undefined until checked, null if it could not be read. */
  serverBuild?: string | null;
  storeId: string | null;
  /** When this tab last auto-reloaded to recover a chunk (epoch ms), or null. */
  reloadedAt: number | null;
  online: boolean;
  userAgent: string;
  /** undefined until the async check has run. */
  probes?: AssetProbe[];
}

const STACK_LINES = 8;
const COMPONENT_LINES = 6;
const MYT_OFFSET_MS = 8 * 60 * 60 * 1000;

/** "2026-10-14 18:01:12 MYT" — the seller's own clock, so "tadi pukul 6" lines up. */
export function formatMyt(ms: number): string {
  return new Date(ms + MYT_OFFSET_MS).toISOString().replace("T", " ").slice(0, 19) + " MYT";
}

function stackLines(input: ErrorReportInput): string[] {
  const lines = (input.stack ?? "").split("\n").map((line) => line.trimEnd());
  // Chrome repeats "Name: message" as the stack's first line; it is already the
  // report's first line.
  if (lines[0]?.includes(input.message)) lines.shift();
  return lines.filter((line) => line.trim()).slice(0, STACK_LINES);
}

function componentLines(componentStack?: string): string[] {
  return (componentStack ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, COMPONENT_LINES)
    .map((line) => `  ${line}`);
}

function versionLine(build: string | null, serverBuild: string | null | undefined): string {
  const running = build ?? "tidak diketahui";
  if (serverBuild === undefined) return `Versi: ${running} · server: menyemak…`;
  if (serverBuild === null) return `Versi: ${running} · server: tak dapat disemak`;
  if (build && serverBuild === build) return `Versi: ${running} · server: sama`;
  return `Versi: ${running} · server: ${serverBuild} (tab ini versi lama)`;
}

function reloadLine(reloadedAt: number | null, at: number): string {
  if (reloadedAt === null) return "Auto-reload: belum dicuba";
  const seconds = Math.max(0, Math.round((at - reloadedAt) / 1000));
  return `Auto-reload: dicuba ${seconds}s sebelum ralat ini`;
}

function probeLines(probes: AssetProbe[] | undefined): string[] {
  if (probes === undefined) return ["Fail: menyemak…"];
  return probes.map((probe) => `Fail: ${probe.url} · cache ${probe.cached} · rangkaian ${probe.network}`);
}

export function formatErrorReport(input: ErrorReportInput): string {
  const components = componentLines(input.componentStack);
  return [
    `${input.name}: ${input.message}`,
    ...stackLines(input),
    ...(components.length ? ["Komponen:", ...components] : []),
    `URL: ${input.path}`,
    `Masa: ${formatMyt(input.at)}`,
    versionLine(input.build, input.serverBuild),
    `Kedai: ${input.storeId ?? "-"}`,
    reloadLine(input.reloadedAt, input.at),
    `Talian: ${input.online ? "online" : "OFFLINE"}`,
    ...probeLines(input.probes),
    `UA: ${input.userAgent}`,
  ].join("\n");
}

/** "200 text/html" from a response, dropping the charset. */
export function describeResponse(status: number, contentType: string | null): string {
  const type = (contentType ?? "").split(";")[0].trim();
  return type ? `${status} ${type}` : String(status);
}

/** The build id stamped into an index.html (`<meta name="app-build" content="…">`). */
export function readBuildMeta(html: string): string | null {
  return html.match(/<meta\s+name="app-build"\s+content="([^"]*)"/)?.[1] || null;
}
